import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = () => {
  const { language, t } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Fallback data
        setFaqs([
          {
            id: "faq-1",
            question: {
              es: "¿Dónde empieza el tour?",
              en: "Where does the tour start?",
              fr: "Où commence la visite ?"
            },
            answer: {
              es: "Te recogemos en tu hotel si está en las zonas de Centro, Salamanca o Retiro.",
              en: "We pick you up at your hotel if it's in Centro, Salamanca, or Retiro areas.",
              fr: "Nous vous récupérons à votre hôtel s'il est dans les quartiers Centro, Salamanca ou Retiro."
            }
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <section 
      id="faq" 
      className="py-24 md:py-32 bg-white"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            FAQ
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917] mb-4">
            {t.faq.title}
          </h2>
          <p className="text-lg text-[#57534E]">
            {t.faq.subtitle}
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-[#FDFBF7] rounded-xl h-20 animate-pulse" />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="bg-[#FDFBF7] rounded-xl px-6 border-none"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="py-5 hover:no-underline group">
                    <span className="text-left font-semibold text-[#1C1917] group-hover:text-[#C2410C] transition-colors">
                      {faq.question[language] || faq.question.es}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-[#57534E] leading-relaxed">
                    {faq.answer[language] || faq.answer.es}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FAQ;
